import React, { Component } from "react";
import { observer } from "mobx-react";

import { countBy } from "@formant/core/common/countBy";
import { defined } from "@formant/core/common/defined";
import { IEventTrigger } from "@formant/core/model/IEventTrigger";
import { severities } from "@formant/core/model/severities";
import { Severity } from "@formant/core/model/Severity";
import { IconName } from "@formant/ui/component/common/Icon";

import { eventTriggerStore, routerStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import { SeverityChip } from "../../common/SeverityChip";
import { SeverityIcon } from "../../common/SeverityIcon";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
    tagDimensions,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { Tags } from "../../common/Tags";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";
import * as styles from "./index.scss";

@observer
export class EventTriggers extends Component {
    @localSettingObservable("settings-events-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-events-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;
        const columns: IDataTableColumn<IEventTrigger>[] = [
            {
                header: "Message",
                row: _ => (
                    <RowTitle
                        title={_.message}
                        icon={
                            _.notificationEnabled ? (
                                <SeverityIcon severity={_.severity} />
                            ) : (
                                "event"
                            )
                        }
                    >
                        {_.notificationEnabled && (
                            <>
                                <div className={styles.severity}>
                                    {_.severity}
                                </div>
                                <SeverityChip severity={_.severity} />
                            </>
                        )}
                    </RowTitle>
                ),
                className: styles.message,
                sortBy: _ => _.message,
            },
            {
                header: "Stream",
                row: _ => _.condition?.stream,
                sortBy: _ => _.condition?.stream,
                className: styles.stream,
                hideBelow: "md",
            },
            {
                header: "Type",
                row: _ => _.eventType,
                sortBy: _ => _.eventType,
                className: styles.type,
                hideBelow: "lg",
            },
            {
                header: "Tags",
                row: _ => <Tags tags={_.tags} />,
                className: styles.tags,
                hideBelow: "lg",
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                className: styles.created,
                hideBelow: "md",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                className: styles.updated,
                hideBelow: "md",
            },
            {
                header: "",
                row: _ => <Actions eventTrigger={_} />,
                className: styles.actions,
            },
        ];

        const eventTriggers = eventTriggerStore.list();

        const totalQuantity = eventTriggers?.length;

        const subtotals =
            eventTriggers &&
            countBy(
                eventTriggers.filter(_ => _.notificationEnabled),
                _ => _.severity,
            );

        const statHeaderGroups = severities.map(severity => ({
            chipClassName: styles[severity],
            icon: eventSeverityIconMap[severity],
            subtotalQuantity: subtotals?.get(severity) || 0,
            subtotalLabel: severity,
        }));

        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions = [
            {
                dimensionKey: "severity",
                dimensionName: "Severity",
                getValue: getSeverity,
                getValueIcon: getSeverityIcon,
            },
            {
                dimensionKey: "trigger",
                dimensionName: "Trigger",
                getValue: getTriggerType,
            },
            {
                dimensionKey: "notificationEnabled",
                dimensionName: "Notification",
                getValue: getNotificationEnabled,
                getValueLabel: (v: boolean) => (v ? "Enabled" : "Disabled"),
            },
            ...tagDimensions(eventTriggers || [], _ => _.tags),
        ];

        const filteredEventTriggers = applySimpleFilter(
            eventTriggers || [],
            dimensions,
            getText,
            filter,
        );

        return (
            <SettingsPage
                title="Event triggers"
                className={styles["event-triggers"]}
                extra={
                    <Help
                        text="An event is an occurrence of something that crosses a specific threshold or meets a specific criteria or pattern, as specified by you. It generates a tick mark on the timeline. You can create, edit, or delete events here, as well as trigger notifications, commands or other actions as part of the event."
                        to="https://docs.formant.io/docs/events#configuring-events"
                    />
                }
                actions={
                    <StandardButton
                        to={routeTo.createEventTrigger()}
                        data-testid="add-event-trigger-button"
                    >
                        Add event trigger
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="event"
                    loading={!eventTriggers}
                    totalQuantity={totalQuantity || 0}
                    totalLabel="Event triggers"
                    groups={statHeaderGroups}
                    className={styles["stats-header"]}
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={eventTriggers || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filteredEventTriggers || []}
                        columns={columns}
                        loading={eventTriggers === undefined}
                        noResultsText="No events triggers found"
                        onRowClicked={this.onEdit}
                        className={styles.table}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (eventTrigger: IEventTrigger) => {
        const id = defined(eventTrigger.id);
        const { navigate, routeTo } = routerStore;
        await navigate(routeTo.editEventTrigger({ id }, { tab: "Triggers" }));
    };
}

const eventSeverityIconMap: { [severity: string]: IconName } = {
    info: "event",
    warning: "warning",
    error: "error",
    critical: "critical",
};

function getText(eventTrigger: IEventTrigger): string {
    return eventTrigger.message;
}

function getSeverity(eventTrigger: IEventTrigger): Severity | undefined {
    const { notificationEnabled, severity } = eventTrigger;
    return notificationEnabled ? severity : undefined;
}

function getSeverityIcon(severity: Severity): IconName | undefined {
    return eventSeverityIconMap[severity];
}

function getTriggerType(eventTrigger: IEventTrigger): string | undefined {
    return eventTrigger?.condition?.type;
}

function getNotificationEnabled(eventTrigger: IEventTrigger): boolean {
    return eventTrigger.notificationEnabled;
}
